/**
 * VisibilityPauseOverlay
 *
 * Shown when the user returns to the tab after switching away.
 * Displays a centered play button over the player so the user
 * can resume intentionally — no surprise audio.
 */

import { useEffect, useCallback } from 'react';
import { Play } from 'lucide-react';
import { cn } from '@/lib/utils';

interface VisibilityPauseOverlayProps {
  /** Called when the user clicks the play button or presses space */
  onResume: () => void;
  className?: string;
}

export function VisibilityPauseOverlay({
  onResume,
  className,
}: VisibilityPauseOverlayProps) {
  // Resume on spacebar press
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.code === 'Space' || e.key === ' ') {
        e.preventDefault();
        onResume();
      }
    },
    [onResume]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return (
    <div
      className={cn(
        'absolute inset-0 z-40 flex flex-col items-center justify-center',
        'bg-black/40 backdrop-blur-[2px]',
        'animate-in fade-in duration-300',
        'cursor-pointer',
        className
      )}
      onClick={onResume}
    >
      {/* Play button circle */}
      <div className="flex items-center justify-center w-16 h-16 rounded-full bg-white/90 shadow-lg transition-transform hover:scale-110 active:scale-95">
        <Play className="h-7 w-7 text-black ml-1" fill="currentColor" />
      </div>

      {/* Label */}
      <p className="mt-3 text-white/70 text-sm select-none">
        Paused while you were away
      </p>
    </div>
  );
}
